import {
  Media, MediaSize, MediaContentTypeType, MediaClazzType, MediaLocation, MediaResolved, MediaClazzTypeValues
} from './medias.models';


export const MEDIAS_MAX_SIZE = Number.MAX_SAFE_INTEGER;

export class MediasHelpers {

  /**
   * Función que devuelve la lista de medias para una localización concreta.
   * Los medias se devuelven con el modelo MediaResolved y su path (ruta) se devuelve completamente calculada, lista para ser renderizada.
   *
   * @param {Media[]} medias Lista de medias de un producto
   * @param {MediaLocation} location Indica en qué localización (widget) queremos devolver los medias
   * @param {number} [width] Tamaño que va a ocupar el media, por defecto el tamaño máximo posible. Se devolverá el tamaño que más se ajuste
   * @param {MediaClazzType} [clazzType] Indica si queremos devolver solo los medias de un clazz concreto (opcional)
   * @param {MediaContentTypeType} [contentTypeType] Indica si queremos devolver solo las imágenes o los videos (opcional)
   * @param {number} set Set you want to retrieve the medias for, defaults to the first set on the media array
   */
  public static mediasResolved(
    medias: Media[],
    location: MediaLocation,
    width?: number,
    clazzType?: MediaClazzType | string,
    contentTypeType?: MediaContentTypeType,
    set?: number
  ): MediaResolved[] {
    set = set ?? medias[0]?.set;
    const mediasResolved: MediaResolved[] = [];

    medias = medias.filter((itMedia) => itMedia.location === location);

    if (set != null) {
      medias = medias.filter((itMedia) => itMedia.set === set);
    }

    if (isDefined(contentTypeType)) {
      medias = medias.filter((itMedia) => itMedia.contentType.type === contentTypeType);
    }

    if (isDefined(clazzType)) {
      medias = medias.filter((itMedia) => ((clazzType in MediaClazzTypeValues)
        ? itMedia.clazz.type === clazzType
        : itMedia.path.includes(clazzType)));
    }

    for (const media of medias) {
      if (media.isFallback) { // filter all fallbacks with a xmedia equivalent
        const hasNonFallbackEquivalent = this.existsNonFallbackEquivalent(mediasResolved, media);
        if (hasNonFallbackEquivalent) {
          continue;
        }
      }

      const mediaResolved = this.calculateMediaResolved(media, location, width);

      if (isDefined(mediaResolved)) {
        mediasResolved.push(mediaResolved);
      }
    }

    mediasResolved.sort((m1, m2) => ((m1.position > m2.position) ? 1 : -1));

    return mediasResolved;
  }

  /**
   * Función que devuelve el primer media de la lista de medias para una localización concreta.
   * El media se devuelve con el modelo MediaResolved y su path (ruta) se devuelve completamente calculada, lista para ser renderizada.
   *
   * @param {Media[]} medias Lista de medias de un producto
   * @param {MediaLocation} location Indica en qué localización (widget) queremos devolver los medias
   * @param {number} [width] Tamaño que va a ocupar el media, por defecto el tamaño máximo posible. Se devolverá el tamaño que más se ajuste
   * @param {MediaClazzType} [clazzType] Indica si queremos devolver solo los medias de un clazz concreto (opcional)
   * @param {MediaContentTypeType} [contentTypeType] Indica si queremos devolver solo las imágenes o los videos (opcional)
   */
  public static firstMediaResolved(
    medias: Media[],
    location: MediaLocation,
    width?: number,
    clazzType?: MediaClazzType | string,
    contentTypeType?: MediaContentTypeType,
    set?: number,
  ): MediaResolved | undefined {
    const mediasResolved = this.mediasResolved(medias, location, width, clazzType, contentTypeType, set);
    const firstMediaResolved = mediasResolved.length > 0 ? mediasResolved[0] : this.loadMainMedia(medias, location, width, contentTypeType, set);
    return firstMediaResolved;
  }

  // ---- Private functions ----
  private static existsNonFallbackEquivalent(mediasResolved: MediaResolved[], media: Media) {
    const hasEquivalentNonFallBackMedia = mediasResolved.some((m) => m.location === media.location
        && m.position === media.position
        // m.clazz === media.clazz && // TODO: comment this until we manage all fallbacks
        && m.contentType.type === media.contentType.type
        && !m.isFallback);

    return hasEquivalentNonFallBackMedia;
  }

  private static calculateMediaResolved(media: Media, location: MediaLocation, width?: number): MediaResolved | undefined {
    let mediaResolved: MediaResolved | undefined;

    const isFallback = media.isFallback;
    const clazz = media.clazz;
    const contentType = media.contentType;
    const metadata = media.metadata;
    const position = media.position;
    const extraInfo = media.extraInfo;
    let path = media.path;

    if (isDefined(media.sizes)) {
      width = width || MEDIAS_MAX_SIZE;
      const size = this.calculateClosestSize(media.sizes, width);
      if (isDefined(size)) {
        path = media.path.replace('{{sizeCode}}', size.code);
        mediaResolved = {
          path, isFallback, clazz, contentType, location, position, size, metadata, extraInfo
        };
      }
    } else {
      mediaResolved = {
        path, isFallback, clazz, contentType, location, position, metadata, extraInfo
      };
    }
    return mediaResolved;
  }

  private static calculateClosestSize(sizes: MediaSize[], desiredWidth: number): MediaSize | undefined {
    let closestSize: MediaSize | undefined;
    let closestDiff = MEDIAS_MAX_SIZE;

    for (const size of sizes) {
      const currentDiff = Math.abs(size.width - desiredWidth);
      if (currentDiff < closestDiff) {
        closestDiff = currentDiff;
        closestSize = size;
      }
    }

    return closestSize;
  }

  private static loadMainMedia(
    medias: Media[],
    location: MediaLocation,
    width?: number,
    contentTypeType?: MediaContentTypeType,
    set?: number,
  ): MediaResolved | undefined {
    return this.mediasResolved(medias, location, width, undefined, contentTypeType, set)[0];
  }

}
